import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { engageAPI } from '../lib/api';
import { isLoggedIn } from '../lib/auth';
import { t } from '../lib/i18n';

// Header bell: unread badge + dropdown; opening marks everything read
// (B5 — the platform notifications pattern, kept deliberately simple).
export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!isLoggedIn()) return;
    engageAPI.notifications()
      .then(({ data }) => { setItems(data.notifications); setUnread(data.unread); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!isLoggedIn()) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      engageAPI.markRead().then(() => setUnread(0)).catch(() => {});
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <button onClick={toggle} className="lms-hit relative inline-flex text-lms-muted hover:text-lms-text" title={t('Notifications')} aria-label={t('Notifications')}>
        <Bell size={17} />
        {unread > 0 && (
          <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-lms-accent px-1 text-[10px] font-semibold text-lms-accent-ink lms-num">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-50 w-80 overflow-hidden rounded-lg border border-lms-border bg-lms-surface shadow-[var(--lms-elev-3)]">
          <div className="border-b border-lms-border px-4 py-2.5 text-xs font-semibold text-lms-muted">
            {t('Notifications')}
          </div>
          {items.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-lms-faint">{t('Nothing yet.')}</p>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {items.map((n) => (
                <Link
                  key={n.id}
                  to={n.link || '#'}
                  onClick={() => setOpen(false)}
                  className={`block border-b border-lms-border px-4 py-2.5 text-sm last:border-b-0 hover:bg-lms-raised/60 ${n.read ? 'text-lms-muted' : 'text-lms-text'}`}
                >
                  <div className="line-clamp-2">{n.title}</div>
                  <div className="mt-0.5 text-[10px] text-lms-faint lms-num">{(n.created_at || '').slice(0, 16).replace('T', ' ')}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
